import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalContext } from '../context/GlobalContext';
import { useSelfcareApi } from '../services/SelfcareApiService';
import { useTranslation } from 'react-i18next';


const RECAPTCHA_ACTIVATED = process.env.REACT_APP_RECAPTCHA_ACTIVATED === 'true';
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

function Home() {
  const navigate = useNavigate();
  const { siteInfo, currentLang, getHomeContent, setReady } = useGlobalContext();
  const selfcareApi = useSelfcareApi();
  const { t } = useTranslation();
  
  const [user, setUser] = useState({ email: '' });
  const [step, setStep] = useState('home');
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (RECAPTCHA_ACTIVATED && RECAPTCHA_SITE_KEY) {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
      document.body.appendChild(script);
    }
  }, []);


  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const sendLink = async (recaptchaToken = null) => {
    try {
      setIsSubmitting(true);
      const response = await selfcareApi.sendLink(user.email, recaptchaToken);
      
      if (response.status === 'ok') {
        setStep('confirm_sent');
      } else {
        navigate('/error');
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      navigate('/error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = {};
    if (!user.email) {
      errors.email = 'L\'adresse email est requise';
    } else if (!validateEmail(user.email)) {
      errors.email = 'L\'adresse email n\'est pas valide';
    }

    setFormErrors(errors);

    if (Object.keys(errors).length === 0) {
      if (RECAPTCHA_ACTIVATED && RECAPTCHA_SITE_KEY) {
        window.grecaptcha.ready(() => {
          window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'selfcare_send' })
            .then((token) => {
              sendLink(token);
            })
            .catch((error) => {
              console.error('Erreur reCAPTCHA:', error);
              sendLink();
            });
        });
      } else {
        await sendLink();
      }
    }
  };

  const handleReset = () => {
    setUser({ email: '' });
    setStep('home');
    setFormErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUser(prev => ({ ...prev, [name]: value }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const homeContent = getHomeContent();

  if (!siteInfo) {
    return <div>Loading...</div>;
  }

  return (
    <div id="home">
      {step === 'home' && (
        <>
          <h1 className="text-center">
            {"Selfcare "+ siteInfo.libelle}
          </h1>
          
          <section id="form">
            <div className="text-right">
              <div>
                <img src={siteInfo.mainImage} alt="" />
              </div>
            </div>
            
            <div>
              {homeContent.teaser && (
                <div dangerouslySetInnerHTML={{ __html: homeContent.teaser }} />
              )}
              
              {homeContent.text && (
                <div dangerouslySetInnerHTML={{ __html: homeContent.text }} />
              )}
              
              <h2>{t('Demander mon accès')}</h2>
              
              <form onSubmit={handleSubmit} className="css-form" noValidate>
                <div className={`form-group ${formErrors.email ? 'has-error' : ''}`}>
                  <label className="control-label" htmlFor="email">
                    {t('Adresse email')}
                  </label>
                  <input
                    className="form-control"
                    name="email"
                    id="email"
                    type="email"
                    required
                    value={user.email}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                  />
                  {formErrors.email && (
                    <span className="help-block">{formErrors.email}</span>
                  )}
                </div>
                
                <div className="text-center">
                  <button
                    type="submit"
                    className="btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting 
                      ? t('Envoi en cours...') 
                      : t('Voir mes bons d\'achat')
                    }
                  </button>
                </div>
              </form>
            </div>
          </section>
        </>
      )}
      
      {step === 'confirm_sent' && (
        <section id="confirm">
           <h1 className="sr-only">confirm Sent</h1>
          <div>
            
            <div>
              <img src="/assets/images/mail.svg" alt="" />
            </div>
            <h2>{t('Accès envoyé')}</h2>
            <p>{t('Un message vous a été envoyé à l\'adresse')} {user.email}.</p>
            <p>{t('Pour accéder à la liste de vos bons d\'achat, veuillez suivre les instructions fournies dans ce message')}</p>
            <p>
              {t("L'email que vous allez recevoir peut éventuellement arriver dans votre dossier spams, promotions ou indésirables")}<br />
              {t('Veillez à bien vérifier ces dossiers également.')}
            </p>
            <p>
              {t('Si vous ne recevez pas cet email, veuillez procéder à une ')}
              <button 
                type="button" 
                onClick={handleReset}
                className="link-button"
              >
                {t('nouvelle demande d\'accès')}
              </button>
            </p>
          </div>
        </section>
      )}
    </div>
  );
}

export default Home;